import { confirmSignIn, rememberDevice, signIn } from '@aws-amplify/auth';
import { useMsal } from '@azure/msal-react';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import SixCodeTextField from 'components/SixCodeTextField/SixCodeTextField';
import { loginRequest } from 'environment/SsoConfig';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { triggerFetchToken } from 'state/actions/graphqlToken';
import MicrosofLogo from 'v2/assets/vector/MicrosoftLogo';
import TKHSecurityLogo from 'v2/assets/vector/TKHSecurityLogo';
import TKHSecurityLogoDark from 'v2/assets/vector/TKHSecurityLogoDark';
import AuthLayout from 'v2/components/AuthLayout/AuthLayout';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { FORGOT_PASSWORD_PATH } from 'v2/paths';

const Login: React.FC<React.PropsWithChildren> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [confirmCode, setConfirmCode] = useState<string[]>(Array(6).fill(''));
  const [error2MFA, setError2MFA] = useState<boolean>(false);
  const [isConfirmWithAuthenticatorEnabled, setIsConfirmWithAuthenticatorEnabled] = useState<boolean>(false);
  const [isRememberDeviceChecked, setIsRememberDeviceChecked] = useState<boolean>(false);
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const [showPassword, setShowPassword] = useState(false);
  const { translation } = useTranslation();
  const { instance } = useMsal();

  const handleLoginSSO = async () => {
    await instance.loginRedirect(loginRequest);
  };

  const handleLoginEmailPassword = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (email && password) {
      event.preventDefault();
      setError(undefined);
      try {
        const data = await signIn({ username: email, password });
        if (rememberMe) {
          localStorage.setItem('email', email);
        }
        if (data) {
          if (data.isSignedIn === false && data.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE')
            setIsConfirmWithAuthenticatorEnabled(true);
          else triggerFetchToken();
        }
      } catch (err) {
        if (err instanceof Error) {
          const errorName = err.name;
          const errorTypes = ['NotAuthorizedException', 'UserNotFoundException'];
          if (errorTypes.includes(errorName)) {
            setError('Email and password combination incorrect');
            return;
          }
        }
        handleFetchError('' + err);
      }
    }
  };

  const handleConfirmCode = async () => {
    if (confirmCode) {
      try {
        const data = await confirmSignIn({
          challengeResponse: confirmCode.join(''),
          options: isRememberDeviceChecked ? { rememberDevice: true } : undefined
        });
        if (data)
          if (isRememberDeviceChecked) {
            await rememberDevice();
          }
        triggerFetchToken();
      } catch (err) {
        if (err instanceof Error) {
          setError2MFA(true);
        } else handleFetchError('' + err);
      }
    }
  };

  const handleResendCode = async () => {
    if (email) {
      try {
        await signIn({ username: email, password });
      } catch (err) {
        handleFetchError('' + err);
      }
    }
  };

  const handleForgotPassword = () => {
    navigate(FORGOT_PASSWORD_PATH);
  };

  React.useEffect(() => {
    const savedEmail = localStorage.getItem('email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <AuthLayout paperPadding="40px" paperWidth="448px" windowId="login-window" showFooter>
      {isConfirmWithAuthenticatorEnabled ? (
        <Stack flexDirection="column" gap="40px">
          <Stack justifyContent="space-between" alignItems="center">
            <Typography variant="h4" color="textPrimary">
              ATLAS
            </Typography>
            <TKHSecurityLogoDark sx={{ height: 40, width: 106 }} />
          </Stack>
          <Stack flexDirection="column" gap="24px">
            <Typography>{translation.enter_verification_code}</Typography>
            <Stack flexDirection="column" gap="16px">
              <SixCodeTextField
                code={confirmCode}
                setCode={setConfirmCode}
                triggerWhenCodeIsFull={handleConfirmCode}
                setError={setError2MFA}
              />
              {error2MFA && (
                <Alert
                  variant="standard"
                  severity={'error'}
                  style={{
                    border: `1px solid ${theme.palette.error.main}`
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.error.main
                    }
                  }}
                >
                  <Stack flexDirection="column">
                    <Typography variant="h6">{translation.invalid_verification_code}</Typography>
                    <Typography variant="body2">{translation.verification_code_hint}</Typography>
                  </Stack>
                </Alert>
              )}
              <Stack alignItems="center" gap="8px" flexDirection="row">
                <Checkbox
                  id="remember-device-checkbox"
                  checked={isRememberDeviceChecked}
                  onChange={(event) => setIsRememberDeviceChecked(event.target.checked)}
                />
                <Typography>{translation.trust_device_for_30_days}</Typography>
                <Tooltip title={translation.trusted_device_hint}>
                  <InfoOutlineIcon />
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack flexDirection="column" gap={2}>
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="h4">ATLAS</Typography>
            <TKHSecurityLogo sx={{ height: 40, width: 106 }} />
          </Stack>
          <Stack flexDirection="column">
            <Stack flexDirection="column" gap={1}>
              <TextField
                id="username"
                type="email"
                label={translation.email_address}
                size="medium"
                variant="outlined"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <TextField
                id="password"
                type={showPassword ? 'text' : 'password'}
                label={translation.password}
                size="medium"
                variant="outlined"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                InputProps={{
                  endAdornment: password ? (
                    <InputAdornment position="end">
                      <IconButton
                        id="toggle-password-visibility-button"
                        aria-label={translation.toggle_password_visibility}
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? (
                          <VisibilityOutlined style={{ color: theme.custom.action.active }} />
                        ) : (
                          <VisibilityOffOutlined style={{ color: theme.custom.action.active }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }}
              />
              {error && (
                <Alert
                  variant="standard"
                  severity={'error'}
                  style={{
                    border: `1px solid ${theme.palette.error.main}`
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.error.main
                    }
                  }}
                >
                  <Stack flexDirection="column">
                    <Typography variant="body2">{error}</Typography>
                  </Stack>
                </Alert>
              )}
              <Grid container direction={{ xs: 'column', sm: 'row' }} sx={{ alignItems: 'center' }}>
                <Grid
                  size={{ xs: 12, sm: 6 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyItems: 'flex-start'
                  }}
                >
                  <Checkbox
                    id="remember-me-checkbox"
                    value={rememberMe}
                    onChange={(event) => setRememberMe(!!event.target.value)}
                  />{' '}
                  <Typography>{translation.remember_me}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: 'right', padding: '0', margin: '0' }}>
                  <Link id="forgot-password-link" onClick={handleForgotPassword} style={{ cursor: 'pointer' }}>
                    <Typography
                      variant="body2"
                      alignSelf="center"
                      color="textPrimary"
                      style={{
                        textDecoration: 'underline',
                        fontStyle: 'solid',
                        offset: '0%',
                        textDecorationThickness: '0%'
                      }}
                    >
                      {translation.forgot_your_password}
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
              <Button
                onClick={handleLoginEmailPassword}
                variant="contained"
                size="large"
                color="primary"
                style={{ color: theme.palette.primary.contrastText }}
                id="sign-in-button"
              >
                {translation.sign_in}
              </Button>
            </Stack>
            <Stack flexDirection="row" gap={1} alignItems="center" justifyContent="center" margin="16px 0 0 0">
              <Divider sx={{ height: '1px', flexGrow: '1' }} />
              <Typography color="textSecondary" variant="body2" sx={{ textAlign: 'center' }}>
                {translation.or_use_sso}
              </Typography>
              <Divider sx={{ height: '1px', flexGrow: '1' }} />
            </Stack>
            <Stack
              id="sso-login-button"
              flexDirection="column"
              gap={1}
              onClick={handleLoginSSO}
              alignSelf="center"
              style={{ cursor: 'pointer' }}
              width="215px"
              height="41px"
              margin="16px 0 0 0"
            >
              <Button size="large" color="secondary" variant="outlined" style={{ gap: '8px' }}>
                <MicrosofLogo id="sign-in-microsoft-button" />
                <Typography color="textPrimary" variant="button">
                  MICROSOFT
                </Typography>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
    </AuthLayout>
  );
};

export default Login;
